import ACTION_TYPE from '../../store/ACTION_TYPE';
import createReducerFunction from '../../store/reducerCreator';

interface Group {
    id: string;
    displayName: string;
}

interface State {
    exists: boolean;
    isAdmin: boolean;
    actsAsAdmin: boolean;
    shouldUpdate: boolean;
    backOfficeUrl: string;
    displayName: string;
    userGroups: Group[];
    connectSession: boolean;
    protectSession: boolean;
    unifiedPlatform: boolean;
    lastFetch: number;
    wasLoggedIn: boolean;
    sideloadingHelpdeskUrl: string;
}

const initialState = {
    exists: false,
    isAdmin: false,
    actsAsAdmin: false,
    shouldUpdate: false,
    backOfficeUrl: '',
    displayName: '',
    userGroups: [],
    connectSession: false,
    protectSession: false,
    unifiedPlatform: true,
    lastFetch: 0,
    wasLoggedIn: false,
    sideloadingHelpdeskUrl: '',
};

interface Action {
    type:
        | ACTION_TYPE.REQUEST_USER_FULFILLED
        | ACTION_TYPE.REQUEST_USER_REJECTED
        | ACTION_TYPE.TOGGLE_ADMIN_MODE
        | ACTION_TYPE.APP_SETTINGS_CHANGED_REJECTED
        | ACTION_TYPE.ADD_SIDELOADED_APP_REJECTED
        | ACTION_TYPE.REUPLOAD_SIDELOADED_APP_REJECTED;
    payload: {
        statusCode: number;
        sideloadingHelpdeskUrl?: string;
        isAdmin?: boolean;
        backOfficeUrl?: string;
        displayName?: string;
        domain?: string;
        groups?: Group[];
        disableUploadingApp?: boolean;
        connectSession?: boolean;
        protectSession?: boolean;
        unifiedPlatform?: boolean;
        banners?: {
            bannerAppId: string;
            bannerUrlBig: string;
            bannerUrlSmall: string;
            egappsUrlBig: string;
            egappsUrlSmall: string;
        };
    };
}

export default createReducerFunction<State, Action>({
    initialState,
    reducers: {
        // eslint-disable-next-line complexity
        [ACTION_TYPE.REQUEST_USER_FULFILLED]: (state, { payload }) => {
            if (!payload) {
                return clearUser(state);
            }

            return {
                exists: true,
                isAdmin: payload.isAdmin ?? false,
                actsAsAdmin: payload.isAdmin ?? false,
                backOfficeUrl: payload.backOfficeUrl || '',
                displayName: payload.displayName || '',
                disableUploadingApp: payload.disableUploadingApp,
                pageDomain: payload.domain,
                userGroups: payload.groups || [],
                connectSession: payload.connectSession ?? false,
                protectSession: payload.protectSession ?? false,
                unifiedPlatform: payload.unifiedPlatform ?? false,
                banners: payload.banners,
                lastFetch: Date.now(),
                shouldUpdate: false,
                wasLoggedIn: true,
                sideloadingHelpdeskUrl: payload.sideloadingHelpdeskUrl ?? '',
            };
        },
        [ACTION_TYPE.REQUEST_USER_REJECTED]: (state) => clearUser(state),
        [ACTION_TYPE.TOGGLE_ADMIN_MODE]: (state) => ({ ...state, actsAsAdmin: !state.actsAsAdmin }),
        [ACTION_TYPE.APP_SETTINGS_CHANGED_REJECTED]: handleActionRejected,
        [ACTION_TYPE.ADD_SIDELOADED_APP_REJECTED]: handleActionRejected,
        [ACTION_TYPE.REUPLOAD_SIDELOADED_APP_REJECTED]: handleActionRejected,
        [ACTION_TYPE.REMOVE_SIDELOADED_APP_REJECTED]: handleActionRejected,
    },
});

function clearUser(state: State) {
    return {
        ...initialState,
        lastFetch: Date.now(),
        shouldUpdate: false,
        wasLoggedIn: state.wasLoggedIn,
        unifiedPlatform: state.unifiedPlatform,
    };
}

function handleActionRejected(state: State, { payload }: Action) {
    if (payload.statusCode === 401) {
        return { ...initialState, shouldUpdate: true, wasLoggedIn: state.wasLoggedIn };
    }

    return state;
}
